import i18n from '@/configuration/i18n/i18n.configuration';
import router from '@/configuration/router';
import RouteNames from '@/configuration/router/names.routes';
import { ModulesEnum } from '@/core/modulesEnum';
import { ErrorWithToastData } from '@/exceptions/ErrorWithToastData';
import { UseCaseKeys, UseCaseKeyType } from '@/models/channel-keys';
import navigationService from '@/services/NavigationService';
import { useAssistantStore } from '@/stores/assistantStore';
import { useAuthStore } from '@/stores/authStore';
import { useToasterStore } from '@/stores/useToasterStore';
import { ErrorUtils } from './error.utils';

export class CommandReceiver {
  public static t = i18n.global.t;
  /**
   * Public method that receives the use case, command, and message.
   * Based on these parameters, it delegates execution to the corresponding function.
   *
   * @param useCase - Identifier of the use case.
   * @param selectedText - The text or payload to be processed.
   * @param command - Identifier of the command (optional).
   * @returns A promise that resolves when the handling is complete.
   */
  public static async Handle(
    useCase: UseCaseKeyType,
    selectedText: string
  ): Promise<void> {
    const { products } = useAuthStore();
    const toaster = useToasterStore();

    try {
      if (!products.modules[ModulesEnum.LEGAL_ASSISTANT])
        throw new ErrorWithToastData(
          this.t('contextual_errors.access.title'),
          this.t('contextual_errors.access.body')
        );

      switch (useCase) {
        case UseCaseKeys.AI_ASSISTANT:
          this.handleLegalAssistant(selectedText);
          break;
        // TODO: Add other cases
        default:
          console.warn(`No handler defined for use case: ${useCase}`);
          break;
      }
    } catch (error) {
      if (error instanceof ErrorWithToastData) {
        toaster.error({ title: error.title, body: error.body });
      } else if (Array.isArray(error)) {
        for (const err of error) {
          const translatedError = ErrorUtils.getTranslatedError(err, this.t);
          toaster.error({
            title: translatedError.title,
            body: translatedError.body
          });
        }
      } else {
        const translatedError = ErrorUtils.getTranslatedError(
          'UNKNOWN',
          this.t
        );
        toaster.error({
          title: translatedError.title,
          body: translatedError.body
        });
      }

      console.error(error);
    }
  }

  private static handleLegalAssistant(selectedText: string): void {
    const store = useAssistantStore();
    const storeActions = store.createAssistantActions();
    storeActions.setSelectedText(selectedText);

    const unallowedRoutes: string[] = [
      RouteNames.Welcome,
      RouteNames.FranceAuth,
      RouteNames.SpainAuth,
      RouteNames.LegalAssistant
    ];

    if (!unallowedRoutes.includes(router.currentRoute.value.name as string)) {
      navigationService.navigateTo({ name: RouteNames.LegalAssistant });
    }
  }
}
